import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { SelectField } from '../components/SelectField';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { messageForError } from '../lib/errors';
import { timezones } from '../lib/format';
import {
  createHouseholdFormSchema,
  joinHouseholdFormSchema,
  parseForm,
  type FieldErrors,
} from '../lib/forms';

const guessTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const { auth, refresh, logout } = useAuth();
  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tzOptions = timezones().map((z) => ({ value: z, label: z.replace(/_/g, ' ') }));

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (mode === 'create') {
      const r = parseForm(createHouseholdFormSchema, e.currentTarget);
      if (r.errors) return setErrors(r.errors);
      setErrors({});
      setBusy(true);
      try {
        await api.households.create(r.data);
        await refresh();
        void navigate('/', { replace: true });
      } catch (err) {
        setError(messageForError(err));
      } finally {
        setBusy(false);
      }
    } else {
      const r = parseForm(joinHouseholdFormSchema, e.currentTarget);
      if (r.errors) return setErrors(r.errors);
      setErrors({});
      setBusy(true);
      try {
        await api.households.join(r.data);
        await refresh();
        void navigate('/', { replace: true });
      } catch (err) {
        setError(messageForError(err));
      } finally {
        setBusy(false);
      }
    }
  }

  const name = auth.status === 'authed' ? auth.user.displayName : '';

  return (
    <AuthLayout
      title={`Welcome${name ? `, ${name}` : ''}`}
      subtitle="A household shares recipes, plans, pantry and grocery lists."
    >
      <div className="grid grid-cols-2 gap-2" role="tablist" aria-label="Create or join">
        {(['create', 'join'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={`min-h-11 rounded-lg border px-3 text-sm ${
              mode === m ? 'border-accent bg-accent-soft text-brand' : 'border-line bg-surface'
            }`}
          >
            {m === 'create' ? 'Start a household' : 'Join with a code'}
          </button>
        ))}
      </div>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4" key={mode}>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {mode === 'create' ? (
          <>
            <Field
              id="name"
              name="name"
              label="Household name"
              placeholder="The Smiths"
              required
              error={errors['name']}
            />
            <SelectField
              id="timezone"
              name="timezone"
              label="Time zone"
              defaultValue={guessTz()}
              options={tzOptions}
              error={errors['timezone']}
            />
            <SelectField
              id="unitSystem"
              name="unitSystem"
              label="Units"
              defaultValue="us"
              options={[
                { value: 'us', label: 'US customary (cups, oz, °F)' },
                { value: 'metric', label: 'Metric (ml, g, °C)' },
              ]}
            />
            <Field
              id="defaultServings"
              name="defaultServings"
              label="Usual number of people at dinner"
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              defaultValue={4}
              error={errors['defaultServings']}
            />
          </>
        ) : (
          <Field
            id="code"
            name="code"
            label="Invite code"
            autoCapitalize="characters"
            autoCorrect="off"
            required
            error={errors['code']}
          />
        )}
        <Button type="submit" disabled={busy}>
          {busy ? 'Working…' : mode === 'create' ? 'Create household' : 'Join household'}
        </Button>
      </form>
      <button
        type="button"
        onClick={() => void logout()}
        className="text-center text-sm text-muted underline-offset-2 hover:underline"
      >
        Sign out
      </button>
    </AuthLayout>
  );
}
